/**
 * Data Transformation Utilities
 * Centralizes field mapping between frontend and backend data structures
 */

/**
 * Location Data
 */
export interface LocationData {
  latitude: number;
  longitude: number;
  timestamp: string;
  accuracy?: number;
  source?: string;
  batteryLevel?: number;
}

/**
 * Backend Protected Person Response (from API)
 */
export interface BackendProtectedPerson {
  id: string;
  firstName: string;
  lastName: string;
  title?: string;
  department?: string; // Primary field name (renamed from organization)
  organization?: string; // Deprecated - for backwards compatibility only
  phone?: string;
  email?: string;
  riskLevel: string;
  profilePictureUrl?: string;
  location?: LocationData | null; // Latest location tracking data
  homeAddressLine1?: string;
  homeAddressLine2?: string;
  homeCity?: string;
  homeState?: string;
  homeZipCode?: string;
  homeCountry?: string;
  emergencyContactName?: string;
  emergencyContactRelationship?: string;
  emergencyContactPhone?: string;
  emergencyContactEmail?: string;
  insuranceProvider?: string;
  insurancePolicyNumber?: string;
  insuranceGroupNumber?: string;
  primaryDoctorName?: string;
  primaryDoctorPhone?: string;
  primaryDoctorAddress?: string;
  medicalConditions?: string | string[];
  medicalDisclosureConsent?: boolean;
  consentDate?: string;
  consentExpiresAt?: string;
  specialRequirements?: string;
  notes?: string; // Backend now sends notes directly (mapped from specialRequirements)
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
}

/**
 * Frontend Protected Person (used in UI)
 */
export interface ProtectedPerson {
  id: string;
  firstName: string;
  lastName: string;
  name: string; // Computed field for display
  title?: string;
  organization?: string;
  phone?: string;
  email?: string;
  riskLevel: string;
  profilePictureUrl?: string;
  location?: LocationData | null; // Latest location tracking data
  homeAddressLine1?: string;
  homeAddressLine2?: string;
  homeCity?: string;
  homeState?: string;
  homeZipCode?: string;
  homeCountry?: string;
  emergencyContactName?: string;
  emergencyContactRelationship?: string;
  emergencyContactPhone?: string;
  emergencyContactEmail?: string;
  insuranceProvider?: string;
  insurancePolicyNumber?: string;
  insuranceGroupNumber?: string;
  primaryDoctorName?: string;
  primaryDoctorPhone?: string;
  primaryDoctorAddress?: string;
  medicalConditions?: string[];
  medicalDisclosureConsent?: boolean;
  consentDate?: string;
  consentExpiresAt?: string;
  specialRequirements?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
}

/**
 * Transform backend protected person to frontend format
 */
export function transformProtectedPerson(
  backend: BackendProtectedPerson
): ProtectedPerson {
  let medicalConditions: string[] = [];
  if (Array.isArray(backend.medicalConditions)) {
    medicalConditions = backend.medicalConditions;
  } else if (typeof backend.medicalConditions === 'string' && backend.medicalConditions.length > 0) {
    try {
      const parsed = JSON.parse(backend.medicalConditions) as unknown;
      medicalConditions = Array.isArray(parsed) ? parsed : [];
    } catch {
      medicalConditions = [];
    }
  }
  return {
    ...backend,
    name: `${backend.firstName} ${backend.lastName}`.trim(),
    // Prefer department if available, fall back to organization for backwards compatibility
    organization: backend.department ?? backend.organization,
    // Backend now sends notes directly; fallback to specialRequirements for backward compatibility
    specialRequirements: backend.notes ?? backend.specialRequirements,
    medicalConditions,
  };
}

/**
 * Transform frontend protected person to backend format (for API requests)
 */
export function transformProtectedPersonToBackend(
  frontend: Partial<ProtectedPerson>
): Partial<BackendProtectedPerson> {
  const { name: _name, medicalConditions, organization, specialRequirements, ...rest } = frontend;

  const backendData: Partial<BackendProtectedPerson> = {
    ...rest,
    medicalConditions: medicalConditions
      ? JSON.stringify(medicalConditions)
      : undefined,
  };

  // Map frontend field names to backend field names
  // organization → department (backend primary field name)
  if (organization !== undefined) {
    backendData.department = organization;
  }

  // specialRequirements is used for notes on the backend
  if (specialRequirements !== undefined) {
    backendData.specialRequirements = specialRequirements;
  }

  // Remove ONLY undefined and null values to avoid sending them in the API request
  // Keep empty strings - let the backend validation handle them
  // This ensures all form fields are sent for proper validation
  const cleanedData: Partial<BackendProtectedPerson> = {};
  for (const [key, value] of Object.entries(backendData)) {
    // Only filter out undefined and null values
    // Keep empty strings, zeros, false, etc. - they are valid values
    // EXCEPT for date fields - convert empty strings to undefined
    if (value !== undefined && value !== null) {
      // Skip empty strings for date fields (they should be undefined or valid ISO dates)
      if ((key === 'consentDate' || key === 'consentExpiresAt') && value === '') {
        continue;
      }
      (cleanedData as Record<string, unknown>)[key] = value;
    }
  }

  return cleanedData;
}

/**
 * Transform array of backend protected persons to frontend format
 */
export function transformProtectedPersons(
  backend: BackendProtectedPerson[]
): ProtectedPerson[] {
  return backend.map(transformProtectedPerson);
}

/**
 * Get full name from first and last name
 */
export function getFullName(firstName?: string, lastName?: string): string {
  if (!firstName && !lastName) {return '';}
  if (!firstName) {return lastName ?? '';}
  if (!lastName) {return firstName;}
  return `${firstName} ${lastName}`.trim();
}

/**
 * Split full name into first and last name
 */
export function splitFullName(fullName: string): { firstName: string; lastName: string } {
  const parts = fullName.trim().split(' ');
  if (parts.length === 0) {
    return { firstName: '', lastName: '' };
  }
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }
  const firstName = parts[0];
  const lastName = parts.slice(1).join(' ');
  return { firstName, lastName };
}

/**
 * Backend Activity Response
 */
export interface BackendActivity {
  id: string;
  protectedPersonId: string;
  activityType: string;
  title: string;
  description?: string;
  startDatetime: string;
  endDatetime?: string;
  location?: string;
  venueId?: string;
  status: string;
  riskAssessment?: string;
  securityRequirements?: string;
  customFields?: string;
  isRecurring?: boolean;
  recurrencePattern?: string;
  recurrenceInterval?: number;
  recurrenceDaysOfWeek?: string;
  recurrenceEndDate?: string;
  parentActivityId?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

/**
 * Frontend Mission
 */
export interface Activity extends BackendActivity {
  protectedPerson?: ProtectedPerson;
}

/**
 * Transform backend activity to frontend format
 */
export function transformActivity(
  backend: BackendActivity & { protectedPerson?: BackendProtectedPerson }
): Activity {
  return {
    ...backend,
    protectedPerson: backend.protectedPerson
      ? transformProtectedPerson(backend.protectedPerson)
      : undefined,
  };
}

/**
 * Transform array of backend activities to frontend format
 */
export function transformMissions(
  backend: (BackendActivity & { protectedPerson?: BackendProtectedPerson })[]
): Activity[] {
  return backend.map(transformActivity);
}

/**
 * Backend User Response
 */
export interface BackendUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles?: string[];
  permissions?: string[];
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Frontend User
 */
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string; // Computed field
  roles?: string[];
  permissions?: string[];
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Transform backend user to frontend format
 */
export function transformUser(backend: BackendUser): User {
  return {
    ...backend,
    name: getFullName(backend.firstName, backend.lastName),
  };
}

/**
 * Transform array of backend users to frontend format
 */
export function transformUsers(backend: BackendUser[]): User[] {
  return backend.map(transformUser);
}
